import React, { useState } from 'react';
import { useDebounce } from 'react-use';
import {
  Autocomplete,
  AutocompleteInputChangeReason,
  AutocompleteProps,
  Stack,
  TextField,
  TextFieldProps,
  Tooltip,
} from '@mui/material';

import { SearchIcon } from '~assets/icons';
import { BaseFilterOption } from '~types';

interface Props<T>
  extends Omit<AutocompleteProps<T, false, boolean, false>, 'renderInput'> {
  onSearching?: (searchString: string) => void;
  textFieldProps?: TextFieldProps;
  tooltipText?: string;
}

export const AutoCompleteSingleChip = <T extends BaseFilterOption>({
  onSearching,
  textFieldProps = {},
  tooltipText = '',
  ...restProps
}: Props<T>) => {
  const [inputValue, setInputValue] = useState('');

  const resetInputValueOnClose = () => {
    onSearching && onSearching('');
  };

  const onInputValueChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (reason === 'input' || reason === 'reset') {
      setInputValue(value);
    }
  };

  useDebounce(
    () => {
      onSearching && onSearching(inputValue);
    },
    500,
    [inputValue],
  );

  return (
    <Autocomplete
      onClose={resetInputValueOnClose}
      onInputChange={onInputValueChange}
      renderOption={(props, option) => {
        return (
          <li
            {...props}
            key={option.id}
            style={{
              height: '100%',
            }}
          >
            {option.name}
          </li>
        );
      }}
      isOptionEqualToValue={(option, value) => {
        return option?.id === value?.id && option?.name === value?.name;
      }}
      getOptionLabel={(option: T) => option.name}
      renderInput={(params) => (
        <Tooltip title={tooltipText}>
          <TextField
            {...params}
            {...textFieldProps}
            InputProps={{
              ...params.InputProps,
              ...textFieldProps.InputProps,
              startAdornment: (
                <Stack marginLeft={1} marginRight={0.5}>
                  <SearchIcon />
                </Stack>
              ),
              sx: {
                borderRadius: '100px',
                padding: '5px 0 !important',
              },
            }}
          />
        </Tooltip>
      )}
      {...restProps}
    />
  );
};
